import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageTitle from "../components/PageTitle";
import useSEO from "../hooks/useSEO";
import imageWhatsapp from "../assets/whatsapp-logo-green.png";
import miodottore from "../assets/miodottore.png";

function Contatti() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  // SEO configuration
  useSEO({
    title: t('contatti.seo.title'),
    description: t('contatti.seo.description'),
    keywords: t('contatti.seo.keywords'),
  });

  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="relative px-4 py-10 z-0">
      <div
        className={`
          transition-all duration-700
          ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
        `}
      >
        <PageTitle title={t('contatti.title')} />
        <div className="flex flex-col md:flex-row gap-8 mt-8">
          {/* MAP SECTION */}
          <div className="w-full xl:w-10/12">
            <div className="rounded-xl overflow-hidden shadow-md">
              <iframe
                title={t('contatti.map.iframeTitle')}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5941.7162922581765!2d12.521928012479961!3d41.87439837112238!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f61f75149b9a9%3A0x8764e7bf4465138c!2sStudio%20Sgarbini!5e0!3m2!1sen!2sit!4v1742067960946!5m2!1sen!2sit"
                className="w-full h-[400px] md:h-[500px] border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            
            {/* STUDIO INFO */}
            <div className="mt-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {t('contatti.info.studioName')}
              </h2>
              <p className="text-lg text-gray-600 mb-3">
                {t('contatti.info.address')}
              </p>
              <div className="text-lg">
                <span className="font-medium text-gray-700">{t('contatti.info.phone')}</span>
                <a
                  href="tel:+39067886902"
                  className="font-bold text-sky-600 hover:text-sky-700 ml-1"
                >
                  {t('contatti.info.phoneNumber')}
                </a>
              </div>
              <div className="text-lg">
                <span className="font-medium text-gray-700">{t('contatti.info.cellPhone')}</span>
                <a
                  href="https://wa.me/393898226214"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-sky-600 hover:text-sky-700 ml-1"
                >
                  {t('contatti.info.cellPhoneNumber')}
                </a>
              </div>
            </div>
          </div>

          {/* INFO SECTION */}
          <div className="w-full xl:w-2/12 space-y-6">
            <div>
              <h3 className="text-lg font-bold  text-gray-800 mb-1">
                {t('contatti.details.hours.title')}
              </h3>
              <div className="text-gray-600 space-y-1">
                <p className="whitespace-nowrap">{t('contatti.details.hours.weekdays')}</p>
                <p className="whitespace-nowrap">{t('contatti.details.hours.weekend')}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {t('contatti.details.directions.title')}
              </h3>
              <div className="text-gray-600 space-y-1">
                <p className="whitespace-nowrap">{t('contatti.details.directions.metro')}</p>
                <p className="whitespace-nowrap">{t('contatti.details.directions.bus')}</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {t('contatti.details.parking.title')}
              </h3>
              <p className="text-gray-600">{t('contatti.details.parking.info')}</p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-3">
                {t('contatti.details.booking.title')}
              </h3>              
              <div id="reservation-options" className="text-gray-600 space-y-2">
                <a 
                  href="tel:+39067886902" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sky-600 hover:text-sky-700 transition-all cursor-pointer font-medium no-underline"
                >
                  <span className="no-underline">📞</span>
                  <span className="underline hover:no-underline">{t('contatti.details.booking.phone')}</span>
                </a>
                <a 
                  href="https://wa.me/393898226214" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-600 hover:text-green-700 underline hover:no-underline transition-all cursor-pointer font-medium"
                >
                  <img src={imageWhatsapp} alt="WhatsApp" className="w-5 h-5" />
                  <span className="pl-1">{t('contatti.details.booking.whatsapp')}</span>
                </a>
                <a 
                  href="https://www.miodottore.it/strutture/odontoiatria-sgarbini?saasonly=true&prevent-patient-app-banner=true&hl=en-IT&gei=VEDpaMLvG8n97_UPk_-wsAc&rwg_token=ACgRB3e2G_vLFmZUbfitPIq8Oezdtd409m7CodsmDlaLajIl1Sflu1tzcZpPo3SZIbN_rBePW4MSIW4Rr2JzsY5uVsLBrHZKhQ%3D%3D#highlight-calendar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sky-600 hover:text-sky-700 underline hover:no-underline transition-all cursor-pointer font-medium"
                >
                  <img src={miodottore} alt="MioDottore" className="w-5 h-5" />
                  <span className="pl-1">{t('contatti.details.booking.miodottore')}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contatti;
